<script setup>
/**
 * FormFieldsetAnchors
 * Manages the list of navigation links for a form.
 * Coordinates with the global store to track and update the active fieldset
 * and trigger scroll-to actions via the clickedAnchor state.
 */
import { computed, getCurrentInstance } from 'vue';
import FormFieldsetAnchor from './FormFieldsetAnchor';

const props = defineProps({
    formId: {
        type: String,
        required: true
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- Computed ---

const formState = computed(() => store.state[props.formId]);

const tableName = computed(() => formState.value?.tableName);

const legends = computed(() => formState.value?.legends);

const activeFieldset = computed(() => store.state.activeFieldsets[tableName.value]);

const showAllFieldsets = computed(() => formState.value?.showAllFieldsets);

// --- Methods ---

/**
 * Determines if an anchor should be highlighted.
 * The first item is active by default if no activeFieldset is set.
 */
const isActive = (legend, index) => {
    return (
        !showAllFieldsets.value &&
        (
            legend === activeFieldset.value ||
            (!activeFieldset.value && index === 0)
        )
    );
};

/**
 * Updates the global state to reflect the selected fieldset.
 * Note: Uses direct assignment as Vue 3 provides native reactivity
 * for adding new properties to objects (replacing this.$set).
 */
const setActiveFieldset = (legend) => {
    store.state.activeFieldsets[tableName.value] = legend;
    formState.value.clickedAnchor = legend;
};
</script>

<template>
    <div ref="element" class="m-3 mt-4 me-2" role="tablist">
        <FormFieldsetAnchor
            v-for="(legend, index) in legends"
            :key="legend"
            :class="{ 'active': isActive(legend, index) }"
            :title="legend"
            @click.native.prevent="setActiveFieldset(legend)"
        />
    </div>
</template>